// Presentation logic for firm_audit_log rows. Pure, so it can be tested
// without a database or a browser.
//
// Three properties of the table drive every decision here, all deliberate
// (see migration 20260801205233):
//
//   * `action` is FREE TEXT. New actions arrive whenever a definer function or
//     a Netlify function starts logging one, and the viewer must not break or
//     hide a row it has never seen. Every lookup falls back.
//   * `actor_user_id` and `target_user_id` are ON DELETE SET NULL, so rows
//     outlive the accounts they mention. Null is normal, not an error.
//   * `project_id` carries no foreign key at all, so an audit row outlives the
//     project it names and there is nothing to join to.

export interface FirmAuditRow {
  id: string;
  action: string;
  actor_user_id: string | null;
  target_user_id: string | null;
  project_id: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export interface AuditPerson {
  id: string;
  full_name: string | null;
  email: string;
}

/**
 * Known actions, newest-feature first. Anything absent falls through to
 * `humanizeAction`, which is why an unrecognised action still reads sensibly
 * rather than showing a raw identifier or vanishing.
 */
const ACTION_LABELS: Record<string, string> = {
  member_capabilities_changed: 'Capabilities changed',
  member_removed: 'Member removed',
  seat_count_changed: 'Seat count changed',
  project_reassigned: 'Project reassigned',
  mfa_policy_changed: 'Security policy changed',
  backup_codes_generated: 'Backup codes generated',
  mfa_recovery_used: 'Backup code used',
  mfa_reset_by_platform_admin: 'MFA reset by TrueLoad support',
  set_retention_policy: 'Data retention policy changed',
};

/** `some_new_action` → `Some new action`. */
export function humanizeAction(action: string): string {
  const spaced = action.replace(/_/g, ' ').trim();
  if (!spaced) return 'Unknown action';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function formatAuditAction(action: string): string {
  return ACTION_LABELS[action] ?? humanizeAction(action);
}

/** Display name for a user id, tolerating both null ids and departed accounts. */
export function resolvePerson(
  userId: string | null,
  people: readonly AuditPerson[]
): string {
  if (!userId) return 'Deleted account';
  const match = people.find(p => p.id === userId);
  if (!match) return 'Former member';
  return match.full_name?.trim() || match.email;
}

/**
 * Who a row affected, preferring an identity captured in `detail`.
 *
 * Removal is the case that needs this: it nulls the target's `firm_id`, so by
 * the time anyone reads the log the id no longer resolves against the firm's
 * member list — the one row type whose purpose is recording who left was the
 * one that could not say. `remove_firm_member()` now captures the name and
 * email at write time, so rows written from that migration onward are
 * self-contained. Older rows still fall back to "Former member", which is
 * honest about what was recorded rather than inventing a name.
 */
export function resolveAuditTarget(
  row: Pick<FirmAuditRow, 'target_user_id' | 'detail'>,
  people: readonly AuditPerson[]
): string {
  if (!row.target_user_id) return 'Deleted account';

  const match = people.find(p => p.id === row.target_user_id);
  if (match) return match.full_name?.trim() || match.email;

  const name = row.detail?.target_name;
  if (typeof name === 'string' && name.trim()) return name.trim();
  const email = row.detail?.target_email;
  if (typeof email === 'string' && email.trim()) return email.trim();

  return 'Former member';
}

function boolLabel(v: unknown): string {
  return v === true ? 'yes' : v === false ? 'no' : String(v);
}

/** `{from, to}` transition pairs, as written by set_firm_member_capabilities. */
function describeTransition(label: string, value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const t = value as { from?: unknown; to?: unknown };
  if (!('from' in t) || !('to' in t)) return null;
  if (t.from === t.to) return null; // unchanged half of the pair — noise
  return `${label} ${boolLabel(t.from)} → ${boolLabel(t.to)}`;
}

/**
 * One human sentence for a row's `detail` blob.
 *
 * Returns '' rather than a placeholder when there is nothing worth saying, so
 * the caller decides how an empty cell looks. Unknown shapes degrade to compact
 * key=value pairs instead of being dropped — an audit trail that silently hides
 * what it does not recognise is worse than one that prints something ugly.
 */
export function summarizeAuditDetail(
  action: string,
  detail: Record<string, unknown> | null | undefined
): string {
  if (!detail || typeof detail !== 'object') return '';

  if (action === 'member_capabilities_changed') {
    const parts = [
      describeTransition('admin', detail.firm_admin),
      describeTransition('seat', detail.has_seat),
    ].filter(Boolean);
    return parts.join(', ');
  }

  if (action === 'member_removed') {
    return detail.seat_freed === true ? 'freed a seat' : 'held no seat';
  }

  if (action === 'seat_count_changed') {
    const to = detail.to;
    const seated = detail.seated;
    const pending = detail.pending_invites;
    if (typeof to !== 'number') return '';
    const held =
      typeof seated === 'number' && typeof pending === 'number'
        ? ` (${seated} in use, ${pending} invited at the time)`
        : '';
    return `now ${to} seats${held}`;
  }

  // set_firm_retention_policy() writes {from:{...}, to:{...}} — nested, so the
  // generic fallback below (which drops object values) would render an empty
  // cell for the one action whose whole point is the numbers that changed.
  if (action === 'set_retention_policy') {
    const from = (detail.from ?? {}) as Record<string, unknown>;
    const to = (detail.to ?? {}) as Record<string, unknown>;
    const parts = [
      describeRetentionWindow('intervals', from.intervals_days, to.intervals_days),
      describeRetentionWindow('identity', from.identity_days, to.identity_days),
    ].filter(Boolean);
    return parts.join(', ');
  }

  if (action === 'mfa_policy_changed') {
    const t = describeTransition('policy', detail.mfa_policy);
    if (t) return t;
    if (typeof detail.to === 'string') return `policy → ${detail.to}`;
    return '';
  }

  // Unknown shape: compact, ordered, and truncated so one enormous blob cannot
  // wreck the table.
  const pairs = Object.entries(detail)
    .filter(([, v]) => v !== null && typeof v !== 'object')
    .map(([k, v]) => `${k.replace(/_/g, ' ')}=${String(v)}`);
  const joined = pairs.join(', ');
  return joined.length > 140 ? `${joined.slice(0, 137)}…` : joined;
}

/** "intervals: off → 365 days", or '' when that window did not change. */
function describeRetentionWindow(
  label: string,
  from: unknown,
  to: unknown
): string {
  const show = (v: unknown) =>
    v === null || v === undefined ? 'off' : `${v} days`;
  if (show(from) === show(to)) return '';
  return `${label}: ${show(from)} → ${show(to)}`;
}

/** Absolute local timestamp — audit trails are evidence, not a social feed. */
export function formatAuditTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
